import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { client as braintreeClient, vaultManager as braintreeVaultManager } from 'braintree-web';
import type { FetchPaymentMethodsPayload, VaultManager } from 'braintree-web';

import {
  useCreateHqSavedPaymentMethodNonceMutation,
  useGetHqBraintreeSavedContextQuery,
} from '@/api/BraintreePaypalQuery';
import type {
  ISavedPaymentMethod,
  SavedPaymentMethodType,
} from '@/common/interfaces/payment.interface';
import {
  cleanSavedPaymentMethods,
  getDefaultSavedPaymentMethod,
  getSavedPaymentMethodFingerprint,
  getSavedPaymentMethodLooseFingerprint,
} from '@/utilities/PaymentUtils';

interface IUseSavedPaymentMethodsOptions {
  enabled: boolean;
  userId?: number;
}

interface IVaultPaymentDetails {
  cardType?: string;
  lastFour?: string;
  expirationMonth?: string;
  expirationYear?: string;
  email?: string;
  username?: string;
}

interface IUseSavedPaymentMethodsResult {
  methods: ISavedPaymentMethod[];
  selected: ISavedPaymentMethod | null;
  isUsingNew: boolean;
  isLoading: boolean;
  selectSaved: (token: string) => void;
  useNew: () => void;
  getNonceForSelected: () => Promise<string>;
  removeUnavailableMethod: (token: string) => void;
  refetch: () => Promise<void>;
}

/** The selected saved method no longer exists for this customer; checkouts drop it from the list. */
export class SavedPaymentMethodUnavailableError extends Error {
  constructor() {
    super('The selected saved payment method is unavailable.');
    this.name = 'SavedPaymentMethodUnavailableError';
  }
}

const getVaultPaymentMethodType = (type: string): SavedPaymentMethodType => {
  if (type === 'CreditCard') return 'credit_card';
  if (type === 'PayPalAccount') return 'paypal';
  if (type === 'VenmoAccount') return 'venmo';
  if (type === 'AndroidPayCard') return 'google_pay';
  return 'unknown';
};

const vaultMethodAsSavedMethod = (
  paymentMethod: FetchPaymentMethodsPayload
): ISavedPaymentMethod => {
  const details = (paymentMethod.details ?? {}) as IVaultPaymentDetails;
  return {
    type: getVaultPaymentMethodType(paymentMethod.type),
    token: '',
    last4: details.lastFour,
    cardType: details.cardType,
    expirationMonth: details.expirationMonth,
    expirationYear: details.expirationYear,
    email: details.email,
    username: details.username,
    isDefault: paymentMethod.default,
  };
};

const findVaultMethod = (
  savedMethod: ISavedPaymentMethod,
  vaultMethods: FetchPaymentMethodsPayload[],
  allowLooseMatch: boolean
): FetchPaymentMethodsPayload | undefined => {
  const exactFingerprint = getSavedPaymentMethodFingerprint(savedMethod);
  const looseFingerprint = getSavedPaymentMethodLooseFingerprint(savedMethod);

  return vaultMethods.find((vaultMethod) => {
    const comparable = vaultMethodAsSavedMethod(vaultMethod);
    if (getSavedPaymentMethodFingerprint(comparable) === exactFingerprint) return true;
    return (
      allowLooseMatch && getSavedPaymentMethodLooseFingerprint(comparable) === looseFingerprint
    );
  });
};

/**
 * Loads, cleans, and tokenizes saved Braintree methods for the two authenticated HQ checkouts.
 * Tokens/nonces remain in component memory and Vault Manager is torn down on logout or user switch.
 */
export const useSavedPaymentMethods = ({
  enabled,
  userId,
}: IUseSavedPaymentMethodsOptions): IUseSavedPaymentMethodsResult => {
  const vaultManagerRef = useRef<VaultManager | null>(null);
  const [methods, setMethods] = useState<ISavedPaymentMethod[]>([]);
  const [selectedToken, setSelectedToken] = useState<string | null>(null);
  const [isVaultLoading, setIsVaultLoading] = useState(false);
  const savedContextQuery = useGetHqBraintreeSavedContextQuery(undefined, {
    skip: !enabled || !userId,
  });
  const savedPaymentData = savedContextQuery.data;
  const isContextLoading = savedContextQuery.isFetching;
  const refetchSavedMethodsQuery = savedContextQuery.refetch;
  const [createServerNonce] = useCreateHqSavedPaymentMethodNonceMutation();

  const teardownVaultManager = useCallback(async () => {
    const { current } = vaultManagerRef;
    vaultManagerRef.current = null;
    if (current) {
      await current.teardown();
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    const initialize = async () => {
      await teardownVaultManager();
      setMethods([]);
      setSelectedToken(null);

      if (!enabled || !userId || !savedPaymentData?.clientToken) return;

      const cleanedMethods = cleanSavedPaymentMethods(savedPaymentData.paymentMethods);
      setIsVaultLoading(true);
      try {
        const client = await braintreeClient.create({
          authorization: savedPaymentData.clientToken,
        });
        const manager = await braintreeVaultManager.create({ client });
        if (cancelled) {
          await manager.teardown();
          return;
        }
        vaultManagerRef.current = manager;
      } catch {
        // Methods the Vault Manager cannot tokenize fall back to a server-minted nonce.
      } finally {
        if (!cancelled) {
          setMethods(cleanedMethods);
          setSelectedToken(getDefaultSavedPaymentMethod(cleanedMethods)?.token ?? null);
          setIsVaultLoading(false);
        }
      }
    };

    initialize().catch(() => {
      // The hook fails soft and leaves new-payment checkout available.
    });
    return () => {
      cancelled = true;
      teardownVaultManager().catch(() => {
        // Teardown is best effort during logout, user switch, or unmount.
      });
    };
  }, [enabled, savedPaymentData, teardownVaultManager, userId]);

  const selected = useMemo(
    () => methods.find((paymentMethod) => paymentMethod.token === selectedToken) ?? null,
    [methods, selectedToken]
  );

  const selectSaved = useCallback(
    (token: string) => {
      if (methods.some((paymentMethod) => paymentMethod.token === token)) {
        setSelectedToken(token);
      }
    },
    [methods]
  );

  const useNew = useCallback(() => setSelectedToken(null), []);

  const removeUnavailableMethod = useCallback((token: string) => {
    setMethods((currentMethods) => {
      const remaining = currentMethods.filter((paymentMethod) => paymentMethod.token !== token);
      setSelectedToken((currentToken) =>
        currentToken === token
          ? getDefaultSavedPaymentMethod(remaining)?.token ?? null
          : currentToken
      );
      return remaining;
    });
  }, []);

  const getNonceForSelected = useCallback(async (): Promise<string> => {
    if (!selected) {
      throw new Error('No saved payment method is selected.');
    }

    if (vaultManagerRef.current) {
      const vaultMethods = await vaultManagerRef.current.fetchPaymentMethods({
        defaultFirst: true,
      });
      const looseFingerprint = getSavedPaymentMethodLooseFingerprint(selected);
      const allowLooseMatch =
        methods.filter(
          (paymentMethod) =>
            getSavedPaymentMethodLooseFingerprint(paymentMethod) === looseFingerprint
        ).length === 1;
      const matchingMethod = findVaultMethod(selected, vaultMethods, allowLooseMatch);
      if (matchingMethod?.nonce) return matchingMethod.nonce;
    }

    try {
      const { nonce } = await createServerNonce(selected.token).unwrap();
      if (nonce) return nonce;
    } catch (error: unknown) {
      if ((error as { status?: number })?.status !== 404) throw error;
    }
    removeUnavailableMethod(selected.token);
    throw new SavedPaymentMethodUnavailableError();
  }, [createServerNonce, methods, removeUnavailableMethod, selected]);

  const refetch = useCallback(async () => {
    if (!enabled || !userId) return;
    await refetchSavedMethodsQuery();
  }, [enabled, refetchSavedMethodsQuery, userId]);

  return {
    methods,
    selected,
    isUsingNew: selected === null,
    isLoading: isContextLoading || isVaultLoading,
    selectSaved,
    useNew,
    getNonceForSelected,
    removeUnavailableMethod,
    refetch,
  };
};
